import { useState, useEffect, useRef } from "react";
import "./ViewCarousel.css";
import "../carouselNavigation/CarouselNavigation.css";
import { ReactComponent as CarouselBase } from "../../assets/images/CarouselBase.svg";
import useRemoteViewActions from "../../hooks/useRemoteViewActions";
import RemoteViewer from "../remoteViewer/RemoteViewer";

// Normalize view name to display settings key
const getDisplaySettingsViewType = viewName => {
  if (!viewName) return null;
  const lower = viewName.toLowerCase();
  if (lower.includes("lateral") || lower.includes("sagittal")) return "lateral";
  if (lower.includes("front") || lower.includes("coronal")) return "frontal";
  if (lower.includes("axial")) return "axial";
  return null;
};

const ViewCarousel = ({ views, activeIndex, onViewClick, renderClient }) => {
  const hasViews = views && views.length > 0;
  const loadedViewsRef = useRef(new Set());

  const { setBrightnessAndContrast } = useRemoteViewActions();

  // Initialize state with activeIndex
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (hasViews && activeIndex !== null && activeIndex < views.length) {
      return activeIndex;
    }
    return 0;
  });

  // Track currently displayed view to prevent reload loops
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    if (hasViews && activeIndex !== null && activeIndex < views.length) {
      setCurrentIndex(activeIndex);
    }
  }, [activeIndex, hasViews, views?.length]);

  // Set the current view ID when index changes
  useEffect(() => {
    if (hasViews && views[currentIndex] && views[currentIndex].viewData) {
      setCurrentViewId(
        views[currentIndex].viewData.viewId || views[currentIndex].key
      );
    }
  }, [currentIndex, hasViews, views]);

  // Mark current view as loaded
  useEffect(() => {
    if (currentViewId && !loadedViewsRef.current.has(currentViewId)) {
      loadedViewsRef.current.add(currentViewId);
    }
  }, [currentViewId]);

  // Apply persisted display settings when view changes
  useEffect(() => {
    const applyPersistedSettings = async () => {
      if (!hasViews || !views[currentIndex] || !renderClient) {
        return;
      }

      const currentView = views[currentIndex];
      const viewId = currentView.viewData?.viewId;
      const instance = currentView.instance;
      const viewName = currentView.viewName || currentView.viewData?.viewName;

      if (!viewId || !instance) {
        return;
      }

      const displaySettingsViewType = getDisplaySettingsViewType(viewName);
      if (!displaySettingsViewType) {
        return;
      }

      const persistedSettings = instance.display_settings?.[displaySettingsViewType];
      if (!persistedSettings) {
        return;
      }

      const { brightness, contrast } = persistedSettings;
      if (brightness === undefined || contrast === undefined) {
        return;
      }

      try {
        await setBrightnessAndContrast(viewId, brightness, contrast);
      } catch (error) {
        console.error("Failed to apply persisted display settings in ViewCarousel:", error);
      }
    };

    applyPersistedSettings();
  }, [currentIndex, views, hasViews, renderClient, setBrightnessAndContrast]);

  const handlePrevClick = () => {
    if (!hasViews) return;
    const newIndex = currentIndex === 0 ? views.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    if (onViewClick) onViewClick(newIndex);
  };

  const handleNextClick = () => {
    if (!hasViews) return;
    const newIndex = currentIndex === views.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    if (onViewClick) onViewClick(newIndex);
  };

  // Key for RemoteViewer that includes the view identifier
  // This helps React understand when to create a new instance vs. updating
  const getViewerKey = () => {
    if (!hasViews || !views[currentIndex]) return "no-view";
    const view = views[currentIndex];
    return `remote-viewer-${view.key || view.viewData?.viewId || currentIndex}`;
  };

  return (
    <div className="view-carousel">
      <div className="carousel-view-container">
        {hasViews && views[currentIndex] ? (
          <RemoteViewer
            key={getViewerKey()}
            remoteRenderClient={renderClient}
            viewData={views[currentIndex].viewData}
          />
        ) : null}
      </div>

      <div className="carousel-navigation">
        <CarouselBase className="carousel-base" />

        {/* Transparent buttons positioned over the arrows */}
        <button
          className="carousel-arrow-button carousel-prev-button"
          onClick={handlePrevClick}
          aria-label="Previous view"
        />
        <button
          className="carousel-arrow-button carousel-next-button"
          onClick={handleNextClick}
          aria-label="Next view"
        />
      </div>
    </div>
  );
};

export default ViewCarousel;
